//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { PojoMap } from 'pojo-maps';
import { PojoSet } from 'pojo-sets';
import twitterText from 'twitter-text';

import { getTwitterUserId } from '@account-settings/redux/twitter/selectors/getTwitterUserId';
import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { getLinkMetadata } from '@apis/twitter-post-details/twitterPostDetailsApi';
import { ITwitterScheduleFailureContext } from '@apis/twitter-scheduled-tweets/twitterScheduledTweetsApiModel';
import { searchUser } from '@apis/twitter-search/twitterSearchApi';
import {
  ITwitterMediaPostConfig,
  UniversalScheduledPostConfigType,
} from '@apis/universal-posts/config/universalScheduledPostConfigModel';
import { isTwitterMediaPostConfig } from '@apis/universal-posts/config/universalScheduledPostsConfigUtils';
import {
  createDraftPosts,
  getDraftPosts,
  updateDraftPost,
} from '@apis/universal-posts/draft-posts/universalDraftPostsApi';
import { ICreateDraftPost } from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import {
  createScheduledPosts,
  deleteScheduledPosts,
  getScheduledPost,
  updateScheduledPost,
} from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApi';
import { ICreateUpdateScheduledPost } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import { getStandardPostDetails } from '@apis/universal-posts/universalPostsUtils';
import {
  getDestinationUrlFromShortlink,
  getShortlink,
} from '@apis/url-tracking/urlTrackingApi';
import { SourceType } from '@apis/url-tracking/urlTrackingApiModel';
import {
  ITwitterUserSuggestion,
  MentionsSuggestionType,
} from '@components/molecules/smart-input/smartInputModel';
import Cuid from '@models/Cuid';
import DraftId from '@models/DraftId';
import { getMediaMetadata } from '@models/IMlsMediaItem';
import PostableId from '@models/PostableId';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import {
  getMediaItemByCuid,
  getMediaItemsByCuid,
  updateMediaItems,
} from '@redux/schedule-media/scheduleMediaSlice';
import { getClientId } from '@redux/selectors/getClientId';
import { PostDetailsSchedulingErrorMessages } from '@twitter/components/post-details/twitterPostDetailsUIConstants';
import { getPostPreviewUrl } from '@twitter/redux/post-details/selectors/getPostPreviewData';
import {
  IComparableTwitterPostDetails,
  IStaticTwitterPostDetails,
  ITwitterLinkPostMetadata,
  ITwitterPostDetailsState,
  TwitterPostDetailsValidationError,
  USER_SEARCH_COUNT,
  fallbackComparableTwitterPostDetails,
  isTwitterMediaTooLarge,
  isTwitterMediaTooSmall,
} from '@twitter/redux/post-details/twitterPostDetailsModel';
import {
  getTwitterCurrentPostDetails,
  getTwitterPostDetailsDraftId,
  getTwitterPostDetailsFlags,
  getTwitterPostDetailsPostId,
} from '@twitter/redux/schedule/selectors/getTwitterPostDetails';
import {
  addSwapDraftToPostFailureToast,
  addSwapDraftToPostSuccessToast,
  addSwapPostToDraftFailureToast,
  addSwapPostToDraftSuccessToast,
} from '@universal-scheduling/redux/drafts/toasts/universalDraftsSwapToast';
import {
  deleteDraftPost,
  fetchDraftById,
} from '@universal-scheduling/redux/drafts/universalDraftsSlice';
import { isDefined } from '@utils/filterPredicates';
import { RequestError } from '@utils/sessionFetch';

import { RoutableStateName } from 'app/top-level-states';

const initialState: ITwitterPostDetailsState = {
  currentPostDetails: fallbackComparableTwitterPostDetails,
  initialPostDetails: fallbackComparableTwitterPostDetails,
  shortLinks: {},
  userSearches: {},
  errors: {
    shortLinkErrors: [],
    userSearchErrors: [],
  },
  flags: {
    hasSetPostDetails: false,
    isFetchingLinkMetadata: false,
    isFetchingSelectedMedia: false,
    isSchedulingTweet: false,
    isDraftingTweet: false,
  },
  validationErrors: PojoSet.empty<TwitterPostDetailsValidationError>(),
  invalidCuids: PojoSet.empty<Cuid>(),
  schedulingEntryPoint: undefined,
};

const { actions, reducer } = createSlice({
  name: SliceNames.TwitterPostDetails,
  initialState,
  reducers: {
    setInitialPostDetails(
      state,
      action: PayloadAction<{
        staticPostDetails: IStaticTwitterPostDetails;
        comparablePostDetails: IComparableTwitterPostDetails;
        setInitialPostDetailsError?: string;
      }>,
    ) {
      const { connections, draftId, postId, postStatus } =
        action.payload.staticPostDetails;
      state.postId = postId;
      state.postStatus = postStatus;
      state.connections = connections;
      state.draftId = draftId;
      state.currentPostDetails = action.payload.comparablePostDetails;
      state.initialPostDetails = action.payload.comparablePostDetails;
      state.errors.setInitialPostDetailsError =
        action.payload.setInitialPostDetailsError;
      state.flags.hasSetPostDetails = true;
    },
    setPostDetailsPreviewUrl(state, action: PayloadAction<string | undefined>) {
      state.linkPostPreviewData = {
        linkMetadata: undefined,
        url: action.payload,
      };
    },
    setScheduledTime(state, action: PayloadAction<Date>) {
      state.currentPostDetails.scheduledTime = action.payload;
      state.validationErrors = PojoSet.difference(
        state.validationErrors,
        PojoSet.from([
          TwitterPostDetailsValidationError.NoPostTime,
          TwitterPostDetailsValidationError.PostTimeInPast,
        ]),
      );
    },
    setText(state, action: PayloadAction<string>) {
      state.currentPostDetails.text = action.payload;
      state.validationErrors = PojoSet.remove(
        state.validationErrors,
        TwitterPostDetailsValidationError.NoMediaOrText,
      );
    },
    requestLinkMetadata(state) {
      state.flags.isFetchingLinkMetadata = true;
    },
    receiveLinkMetadataSucceeded(
      state,
      action: PayloadAction<ITwitterLinkPostMetadata>,
    ) {
      state.flags.isFetchingLinkMetadata = false;
      state.linkPostPreviewData = {
        ...state.linkPostPreviewData,
        linkMetadata: action.payload,
      };
    },
    receiveLinkMetadataFailed(state, action: PayloadAction<string>) {
      state.errors.fetchLinkMetadataError = action.payload;
      state.flags.isFetchingLinkMetadata = false;
      state.linkPostPreviewData = {
        ...state.linkPostPreviewData,
        linkMetadata: undefined,
      };
    },
    requestSelectedMedia(state) {
      state.flags.isFetchingSelectedMedia = true;
      state.validationErrors = PojoSet.difference(
        state.validationErrors,
        PojoSet.from([
          TwitterPostDetailsValidationError.NoMediaOrText,
          TwitterPostDetailsValidationError.MediaTooSmall,
          TwitterPostDetailsValidationError.MediaTooLarge,
        ]),
      );
      state.invalidCuids = PojoSet.empty<Cuid>();
    },
    receiveSelectedMediaSucceeded(state, action: PayloadAction<Cuid[]>) {
      state.flags.isFetchingSelectedMedia = false;
      state.currentPostDetails.cuids.push(...action.payload);
    },
    receiveSelectedMediaFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingSelectedMedia = false;
      state.errors.fetchSelectedMediaError = action.payload;
    },
    removeSelectedCuid(state, action: PayloadAction<Cuid>) {
      const selectedCuids = state.currentPostDetails.cuids;
      state.currentPostDetails.cuids = selectedCuids.filter(
        (c) => c !== action.payload,
      );
      state.errors.scheduleFailureContext = undefined;
      state.errors.scheduleFailureError = undefined;
    },
    requestSaveScheduledTweet(state) {
      state.flags.isSchedulingTweet = true;
      state.errors.scheduleFailureContext = undefined;
      state.errors.scheduleFailureError = undefined;
      state.errors.draftFailureError = undefined;
    },
    saveScheduledTweetSucceeded(state) {
      state.flags.isSchedulingTweet = false;
    },
    saveScheduledTweetFailed(
      state,
      action: PayloadAction<{
        scheduleFailureError: string;
        scheduleFailureContext: ITwitterScheduleFailureContext;
      }>,
    ) {
      state.flags.isSchedulingTweet = false;
      state.errors.scheduleFailureError = action.payload.scheduleFailureError;
      state.errors.scheduleFailureContext =
        action.payload.scheduleFailureContext;
    },
    requestSaveDraftTweet(state) {
      state.flags.isDraftingTweet = true;
      state.errors.draftFailureError = undefined;
      state.errors.scheduleFailureContext = undefined;
      state.errors.scheduleFailureError = undefined;
    },
    receiveSaveDraftTweetSucceeded(state) {
      state.flags.isDraftingTweet = false;
    },
    receiveSaveDraftTweetFailed(
      state,
      action: PayloadAction<{
        draftFailureError: string;
      }>,
    ) {
      state.flags.isDraftingTweet = false;
      state.errors.draftFailureError = action.payload.draftFailureError;
    },
    setInvalidCuids(state, action: PayloadAction<PojoSet<Cuid>>) {
      state.invalidCuids = action.payload;
    },
    setSchedulingEntryPoint(state, action: PayloadAction<RoutableStateName>) {
      state.schedulingEntryPoint = action.payload;
    },
    setValidationErrors(
      state,
      action: PayloadAction<PojoSet<TwitterPostDetailsValidationError>>,
    ) {
      state.validationErrors = action.payload;
    },
    receiveLinkSucceeded(state, action: PayloadAction<Record<string, string>>) {
      state.shortLinks = { ...state.shortLinks, ...action.payload };
    },
    receiveLinkFailed(state, action: PayloadAction<string>) {
      state.errors.shortLinkErrors.push(action.payload);
    },
    receiveUserSearchSucceeded(
      state,
      action: PayloadAction<Record<string, ITwitterUserSuggestion[]>>,
    ) {
      state.userSearches = { ...state.userSearches, ...action.payload };
    },
    receiveUserSearchFailed(state, action: PayloadAction<string>) {
      state.errors.userSearchErrors.push(action.payload);
    },
    resetPostDetails(state) {
      // User searches will not be reset within a user session to prevent clients hitting
      // the Twitter rate limit
      state.currentPostDetails = initialState.currentPostDetails;
      state.errors = initialState.errors;
      state.validationErrors = initialState.validationErrors;
      state.flags = initialState.flags;
      state.linkPostPreviewData = undefined;
    },
    resetPostDetailsPreviewData(state) {
      state.errors.fetchLinkMetadataError = undefined;
      state.flags.isFetchingLinkMetadata = false;
      state.linkPostPreviewData = undefined;
    },
  },
});

export const {
  removeSelectedCuid,
  resetPostDetailsPreviewData,
  setPostDetailsPreviewUrl,
  setScheduledTime,
  setSchedulingEntryPoint,
  setText,
} = actions;

export const setupCreateTweet = createThunk(
  (postTimeMs?: number, targetCuid?: Cuid) => async (dispatch) => {
    const scheduledTime = postTimeMs ? new Date(postTimeMs) : undefined;

    if (targetCuid) {
      await dispatch(getMediaItemByCuid(targetCuid));
    }

    const comparablePostDetails: IComparableTwitterPostDetails = {
      text: '',
      scheduledTime,
      cuids: targetCuid ? [targetCuid] : [],
    };

    dispatch(
      actions.setInitialPostDetails({
        staticPostDetails: {},
        comparablePostDetails,
      }),
    );
  },
);

export const setupCopyScheduledPost = createThunk(
  (copiedScheduledPostId?: PostableId, copiedDraftId?: DraftId) =>
    async (dispatch, getState) => {
      try {
        let scheduledPost;
        if (isDefined(copiedScheduledPostId)) {
          scheduledPost = await getScheduledPost(copiedScheduledPostId);
        } else if (isDefined(copiedDraftId)) {
          scheduledPost = (await getDraftPosts([copiedDraftId]))[0];
        } else {
          throw new Error(`Cannot copy without post or draft id`);
        }

        if (!isDefined(scheduledPost)) {
          return;
        }

        if (scheduledPost.mediaItems) {
          dispatch(updateMediaItems(scheduledPost.mediaItems));
        }

        const { caption, cuids } = getStandardPostDetails(scheduledPost);

        const comparablePostDetails: IComparableTwitterPostDetails = {
          text: caption ?? '',
          cuids: cuids ?? [],
        };

        dispatch(
          actions.setInitialPostDetails({
            staticPostDetails: {},
            comparablePostDetails,
          }),
        );
      } catch (_e) {
        dispatch(
          actions.setInitialPostDetails({
            staticPostDetails: {},
            comparablePostDetails: fallbackComparableTwitterPostDetails,
            setInitialPostDetailsError: `Failed to copy post`,
          }),
        );
      }
    },
);

export const setupEditTweet = createThunk(
  (postId: PostableId) => async (dispatch, getState) => {
    const fetchScheduledTweet = async (postId: PostableId) => {
      const scheduledPost = await getScheduledPost(postId);
      if (isTwitterMediaPostConfig(scheduledPost.config)) {
        return {
          id: scheduledPost.id,
          postStatus: scheduledPost.status,
          scheduledTime: scheduledPost.scheduledTime,
          connections: scheduledPost.connections,
          tweet: {
            cuids: scheduledPost.config.cuids,
            text: scheduledPost.config.text,
            mediaItem: scheduledPost.mediaItems,
          },
        };
      } else {
        throw new Error(`Invalid scheduled post format`);
      }
    };

    try {
      const scheduledTweet = await fetchScheduledTweet(postId);
      if (scheduledTweet.tweet.mediaItem) {
        dispatch(updateMediaItems(scheduledTweet.tweet.mediaItem));
      }

      const staticPostDetails: IStaticTwitterPostDetails = {
        postId,
        postStatus: scheduledTweet.postStatus,
        connections: scheduledTweet.connections,
      };
      console.log('static', staticPostDetails);
      const comparablePostDetails: IComparableTwitterPostDetails = {
        text: scheduledTweet.tweet.text,
        cuids: scheduledTweet.tweet.cuids,
        scheduledTime: scheduledTweet.scheduledTime,
      };

      dispatch(
        actions.setInitialPostDetails({
          staticPostDetails,
          comparablePostDetails,
        }),
      );
    } catch (_e) {
      dispatch(
        actions.setInitialPostDetails({
          staticPostDetails: {
            postId,
          },
          comparablePostDetails: fallbackComparableTwitterPostDetails,
          setInitialPostDetailsError: `Failed to load in post #${postId}`,
        }),
      );
    }
  },
);

export const setupEditDraft = createThunk(
  (draftId: DraftId) => async (dispatch) => {
    try {
      const draftTweet = await dispatch(fetchDraftById(draftId));

      if (!isDefined(draftTweet)) {
        throw new Error(`Could not load draft post: ${draftId}`);
      }

      if (!isTwitterMediaPostConfig(draftTweet.config)) {
        throw new Error(`Missing X's config for draft post: ${draftId}`);
      }

      const staticPostDetails: IStaticTwitterPostDetails = {
        draftId,
        connections: draftTweet.connections,
      };

      const comparablePostDetails: IComparableTwitterPostDetails = {
        text: draftTweet.config.text,
        cuids: draftTweet.config.cuids,
        scheduledTime: draftTweet.scheduledTime,
      };

      dispatch(
        actions.setInitialPostDetails({
          staticPostDetails,
          comparablePostDetails,
        }),
      );
    } catch (_e) {
      console.error(
        PostDetailsSchedulingErrorMessages.RequestLoadDraftTweetFailed,
      );
      dispatch(
        actions.setInitialPostDetails({
          staticPostDetails: {},
          comparablePostDetails: fallbackComparableTwitterPostDetails,
          setInitialPostDetailsError: `Failed to load in draft post #${draftId}`,
        }),
      );
    }
  },
);

/*
 * Loads the hydrated MLS media items associated with the given content drawer
 * media into the schedule media store, and registers the associated cuids
 * as selected upon success.
 */
export const fetchAndSetSelectedCuids = createThunk(
  (contentDrawerItems: IContentLibraryItem[]) => async (dispatch, getState) => {
    dispatch(actions.requestSelectedMedia());

    const clientId = getClientId(getState());
    const newCuids = contentDrawerItems.map((item) =>
      Cuid.build(item, clientId),
    );
    const dedupedCuids = Array.from(new Set(newCuids));
    try {
      await dispatch(getMediaItemsByCuid(dedupedCuids));

      dispatch(actions.receiveSelectedMediaSucceeded(dedupedCuids));
    } catch (_e) {
      dispatch(
        actions.receiveSelectedMediaFailed(
          'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export const fetchPostDetailsLinkMetadata = createThunk(
  () => async (dispatch, getState) => {
    dispatch(actions.requestLinkMetadata());

    const url = getPostPreviewUrl(getState());

    if (url) {
      try {
        const res = await getLinkMetadata({ url });

        if (res) {
          const currentPreviewUrl = getPostPreviewUrl(getState());

          // If the preview URL has changed since the metadata fetch was initiated, short circuit the return
          if (currentPreviewUrl !== url) {
            return;
          }

          const previewLinkMetadata: ITwitterLinkPostMetadata = {
            description: res.description,
            destinationUrl: res.destinationUrl,
            // use the first image passed through the Twitter gateway from the crawler service
            imageUrl: res.imageUrls?.[0],
            title: res.title,
          };

          dispatch(actions.receiveLinkMetadataSucceeded(previewLinkMetadata));
        }
      } catch (_e) {
        dispatch(
          actions.receiveLinkMetadataFailed(
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          ),
        );
      }
    }
  },
);

const validatePostDetails = createThunk(() => (dispatch, getState): boolean => {
  const {
    currentPostDetails: { cuids, scheduledTime, text },
  } = getState().twitter.postDetails;
  const { mediaItems } = getState().scheduleMedia;

  let formErrors = PojoSet.empty<TwitterPostDetailsValidationError>();
  let invalidCuids = PojoSet.empty<Cuid>();

  if (text) {
    if (twitterText.isInvalidTweet(text)) {
      formErrors = PojoSet.add(
        formErrors,
        TwitterPostDetailsValidationError.TextTooLong,
      );
    }
  }

  if (cuids.length < 1 && !text) {
    formErrors = PojoSet.add(
      formErrors,
      TwitterPostDetailsValidationError.NoMediaOrText,
    );
  }

  cuids.forEach((cuid) => {
    // Validates all media dimensions
    const mediaItem = PojoMap.get(mediaItems, cuid);
    if (isDefined(mediaItem)) {
      const mediaMetadata = getMediaMetadata(mediaItem);

      if (isDefined(mediaMetadata)) {
        if (isTwitterMediaTooSmall(mediaMetadata)) {
          formErrors = PojoSet.add(
            formErrors,
            TwitterPostDetailsValidationError.MediaTooSmall,
          );
          invalidCuids = PojoSet.add(invalidCuids, cuid);
        }

        if (isTwitterMediaTooLarge(mediaMetadata)) {
          formErrors = PojoSet.add(
            formErrors,
            TwitterPostDetailsValidationError.MediaTooLarge,
          );
          invalidCuids = PojoSet.add(invalidCuids, cuid);
        }
      }
    }
  });

  if (PojoSet.size(invalidCuids) > 0) {
    dispatch(actions.setInvalidCuids(invalidCuids));
  }

  if (!isDefined(scheduledTime)) {
    formErrors = PojoSet.add(
      formErrors,
      TwitterPostDetailsValidationError.NoPostTime,
    );
  } else if (scheduledTime < new Date()) {
    formErrors = PojoSet.add(
      formErrors,
      TwitterPostDetailsValidationError.PostTimeInPast,
    );
  }

  const hasValidationErrors = PojoSet.toArray(formErrors).length > 0;
  if (hasValidationErrors) {
    dispatch(actions.setValidationErrors(formErrors));
    return false;
  }

  return true;
});

const saveScheduledTweet = createThunk(
  () =>
    async (dispatch, getState): Promise<boolean> => {
      dispatch(actions.requestSaveScheduledTweet());

      const {
        currentPostDetails: { cuids, scheduledTime, text },
        postId,
      } = getState().twitter.postDetails;
      const twitterUserId = getTwitterUserId(getState());

      let scheduleTweetPromise;
      if (isDefined(twitterUserId) && isDefined(scheduledTime)) {
        const req: ICreateUpdateScheduledPost = {
          id: postId,
          scheduledTime: scheduledTime,
          postingEntityId: twitterUserId,
          config: {
            cuids,
            text,
            '@type': UniversalScheduledPostConfigType.TwitterMediaPost,
          },
        };
        scheduleTweetPromise = postId
          ? updateScheduledPost(postId, req)
          : createScheduledPosts([req]);
      } else {
        throw new Error(`Invalid state for schedule x's post`);
      }

      try {
        await scheduleTweetPromise;
        dispatch(actions.saveScheduledTweetSucceeded());
        return true;
      } catch (e) {
        const err = e as RequestError;
        dispatch(
          actions.saveScheduledTweetFailed({
            scheduleFailureError:
              'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
            scheduleFailureContext:
              err.context as ITwitterScheduleFailureContext,
          }),
        );
        return false;
      }
    },
);

export const saveDraftTweet = createThunk(
  () =>
    async (dispatch, getState): Promise<boolean> => {
      dispatch(actions.requestSaveDraftTweet());
      const state = getState();
      const twitterUserId = getTwitterUserId(state);
      const { cuids, scheduledTime, text } =
        getTwitterCurrentPostDetails(state);
      const draftId = getTwitterPostDetailsDraftId(state);

      try {
        if (!isDefined(twitterUserId)) {
          throw new Error('Unable to find X User');
        }

        const config: ITwitterMediaPostConfig = {
          cuids,
          text,
          '@type': UniversalScheduledPostConfigType.TwitterMediaPost,
        };
        const request: ICreateDraftPost = {
          config,
          postingEntityId: twitterUserId,
          scheduledTime,
        };

        if (isDefined(draftId)) {
          await updateDraftPost(draftId, config, scheduledTime);
        } else {
          await createDraftPosts([request]);
        }

        dispatch(actions.receiveSaveDraftTweetSucceeded());

        return true;
      } catch (_e) {
        dispatch(
          actions.receiveSaveDraftTweetFailed({
            draftFailureError:
              PostDetailsSchedulingErrorMessages.RequestSaveDraftTweetFailed,
          }),
        );

        return false;
      }
    },
);

export const getOriginalLink = createThunk(
  (shortLink: string) =>
    async (dispatch, getState): Promise<string | undefined> => {
      const { shortLinks } = getState().twitter.postDetails;

      // Check if we already have an originalLink for this shortLink
      if (shortLinks[shortLink]) {
        return shortLinks[shortLink];
      }

      try {
        const originalLink = await getDestinationUrlFromShortlink({
          shortlink: shortLink,
        });

        if (originalLink) {
          dispatch(actions.receiveLinkSucceeded({ [shortLink]: originalLink }));

          return originalLink;
        }

        throw new Error(
          `Cannot get original link for short link: ${shortLink}`,
        );
      } catch (_e) {
        dispatch(
          actions.receiveLinkFailed(
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          ),
        );
      }
    },
);

export const getShortLink = createThunk(
  (originalLink: string) => async (dispatch, getState) => {
    const { shortLinks } = getState().twitter.postDetails;

    // Reverse the shortenedLinks object to by keyed by originalLinks
    const linksByOriginalLink = Object.entries(shortLinks).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [value]: key,
      }),
      {},
    ) as Record<string, string>;

    // Check if there is already a short link for this original link
    if (linksByOriginalLink[originalLink]) {
      return linksByOriginalLink[originalLink];
    }

    try {
      const shortLink = await getShortlink({
        url: originalLink,
        sourceType: SourceType.TwitterPost,
      });

      if (shortLink) {
        dispatch(actions.receiveLinkSucceeded({ [shortLink]: originalLink }));
        return shortLink;
      }

      throw new Error(
        `Cannot get short link for original link: ${originalLink}`,
      );
    } catch (_e) {
      dispatch(
        actions.receiveLinkFailed(
          'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export const getUserSearch = createThunk(
  (query: string) => async (dispatch, getState) => {
    const { userSearches } = getState().twitter.postDetails;

    // Check if there is already results for this query (helps prevent hitting rate limit)
    if (userSearches[query]) {
      return userSearches[query];
    }

    try {
      const searchResults = await searchUser({
        query,
        count: USER_SEARCH_COUNT,
      });

      // Map results to SmartInput suggestion and filter any suggestions without all details
      const modifiedSearchResults = searchResults
        .map((user) => {
          const twitterUserSuggestion: ITwitterUserSuggestion = {
            avatarUrl: user.profileImageUrl?.normal ?? '',
            isVerified: user.verified,
            name: user.name ?? '',
            type: MentionsSuggestionType.TwitterUser,
            username: user.screenName ?? '',
          };
          return twitterUserSuggestion;
        })
        .filter((suggestion) => {
          return suggestion.avatarUrl && suggestion.name && suggestion.username;
        });

      dispatch(
        actions.receiveUserSearchSucceeded({ [query]: modifiedSearchResults }),
      );
      return modifiedSearchResults;
    } catch (_e) {
      dispatch(
        actions.receiveUserSearchFailed(
          'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export const validateAndScheduleTweet = createThunk(
  () =>
    async (dispatch): Promise<boolean> => {
      const isTweetValid = dispatch(validatePostDetails());
      if (isTweetValid) {
        const didScheduleSuccessfully = await dispatch(saveScheduledTweet());
        return didScheduleSuccessfully;
      }

      return false;
    },
);

export const resetPostDetails = createThunk(() => (dispatch) => {
  dispatch(actions.resetPostDetails());
});

export const swapDraftToScheduledTweet = createThunk(
  () =>
    async (dispatch, getState): Promise<boolean> => {
      const state = getState();
      const { isDraftingTweet, isSchedulingTweet } =
        getTwitterPostDetailsFlags(state);
      const draftId = getTwitterPostDetailsDraftId(state);

      // Return early if no draftId exists which means we should be calling a different method
      if (!isDefined(draftId)) {
        return false;
      }

      // Return early if the post is already being saved / scheduled
      if (isSchedulingTweet || isDraftingTweet) {
        return false;
      }

      const didSchedulePost = await dispatch(validateAndScheduleTweet());

      if (!didSchedulePost) {
        // Error handling will come from validateAndSchedulePost
        return false;
      }

      const didDeleteDraft = await dispatch(
        deleteDraftPost(draftId, { shouldShowToast: false }),
      );

      if (didDeleteDraft) {
        dispatch(addSwapDraftToPostSuccessToast());
      } else {
        dispatch(addSwapDraftToPostFailureToast());
      }

      return didDeleteDraft;
    },
);

const deleteScheduledTweet = createThunk(
  (postableId: PostableId) => async () => {
    try {
      await deleteScheduledPosts([postableId]);
      return true;
    } catch (_e) {
      console.error(
        PostDetailsSchedulingErrorMessages.RequestDeleteTweetFailed,
      );
      return false;
    }
  },
);

export const swapScheduledTweetToDraft = createThunk(
  () =>
    async (dispatch, getState): Promise<boolean> => {
      const state = getState();
      const { isDraftingTweet, isSchedulingTweet } =
        getTwitterPostDetailsFlags(state);
      const postId = getTwitterPostDetailsPostId(state);

      // Return early if no postableId exists which means we should be calling a different method
      if (!isDefined(postId)) {
        return false;
      }

      // Return early if the post is already being saved / scheduled
      if (isSchedulingTweet || isDraftingTweet) {
        return false;
      }

      const didSaveDraft = await dispatch(saveDraftTweet());

      if (!didSaveDraft) {
        // Error handling will come from saveDraft
        return false;
      }

      const didDeleteScheduledPost = await dispatch(
        deleteScheduledTweet(postId),
      );

      if (didDeleteScheduledPost) {
        dispatch(addSwapPostToDraftSuccessToast());
      } else {
        dispatch(addSwapPostToDraftFailureToast());
      }

      return didDeleteScheduledPost;
    },
);

export default reducer;
